import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { MapContainer, Marker, Popup, TileLayer } from "react-leaflet";
import {
  AppBar,
  Box,
  Button,
  Dialog,
  IconButton,
  Tab,
  Tabs,
  Toolbar,
  Typography,
} from "@mui/material";
import {
  AccessTimeRounded as TimeIcon,
  ArrowDownwardRounded as ArrowDownIcon,
  ArrowUpwardRounded as ArrowUpIcon,
  ArrowBackRounded as BackIcon,
  BalanceRounded as BalanceIcon,
  CheckCircleRounded as CompleteIcon,
  ChevronRightRounded as ChevronIcon,
  Close as CloseIcon,
  ContactsRounded as ContactIcon,
  DirectionsBoatRounded as VesselIcon,
  EditRounded as EditIcon,
  EmailOutlined as EmailIcon,
  Inventory2Outlined as ContainerIcon,
  LocationOnOutlined as LocationIcon,
  MoreVertRounded as MoreIcon,
  PhoneOutlined as PhoneIcon,
} from "@mui/icons-material";
import MobileBottomNavigation from "../MobileBottomNavigation";
import MobileContainerDetailsDialog from "./MobileContainerDetailsDialog";
import MobileTerminalTimingDialog from "./MobileTerminalTimingDialog";
import MobileVesselPositionGrid from "./MobileVesselPositionGrid";
import MobileVesselLoadStability from "./MobileVesselLoadStability";
import VesselLoadStability from "../../partial/VesselLoadStability";
import { getTerminalCallArrivalStateContainerIds } from "../../Helpers";

const CONTAINER_LIST_LIMIT = 15;

type MobileTerminalCallDetailsProps = {
  open: boolean;
  terminalCall: any;
  terminalCalls?: any[];
  terminals?: any[];
  containers: any[];
  vessel?: any;
  onClose: () => void;
  onAdd: () => void;
  onManageContainers?: (terminalCall: any) => void;
};

const getId = (value: any) => {
  const source = typeof value === "string" ? value : value?.["@id"] || value?.id;
  return source ? String(source).split("/").filter(Boolean).pop() : "";
};

const formatDateTime = (value?: string | null) =>
  value && dayjs(value).isValid() ? dayjs(value).format("DD/MM/YYYY HH:mm") : "—";

const timingRows = [
  { key: "planned", label: "Planned", color: "planned" },
  { key: "arrival", label: "Arrival", color: "arrival" },
  { key: "operationStart", label: "Operation start", color: "start" },
  { key: "operationEnd", label: "Operation end", color: "end" },
  { key: "departure", label: "Departure", color: "departure" },
];

const MobileTerminalCallDetails: React.FC<MobileTerminalCallDetailsProps> = ({
  open,
  terminalCall,
  terminalCalls = [],
  terminals = [],
  containers,
  vessel,
  onClose,
  onAdd,
  onManageContainers,
}) => {
  const { t } = useTranslation();
  const [activeCall, setActiveCall] = useState<any>(terminalCall);
  const [cargoTab, setCargoTab] = useState(0);
  const [showAllContainers, setShowAllContainers] = useState(false);
  const [timingOpen, setTimingOpen] = useState(false);
  const [stabilityDetailOpen, setStabilityDetailOpen] = useState(false);
  const [selectedContainer, setSelectedContainer] = useState<any | null>(null);

  useEffect(() => {
    setActiveCall(terminalCall);
    setCargoTab(0);
    setShowAllContainers(false);
  }, [terminalCall]);

  const containerLookup = useMemo(() => {
    const map = new Map<string, any>();
    (containers || []).forEach((container) => {
      const id = getId(container);
      if (id) map.set(id, container);
    });
    return map;
  }, [containers]);

  const resolveContainers = (references: any[] = []) =>
    references.map((reference) => containerLookup.get(getId(reference)) || reference).filter(Boolean);

  const loadingContainers = resolveContainers(activeCall?.loadingContainers || []);
  const unloadingContainers = resolveContainers(activeCall?.unloadingContainers || []);
  const visibleContainers = cargoTab === 0 ? loadingContainers : unloadingContainers;
  const vesselForStability = vessel || activeCall?.vessel;
  const stabilityContainers = useMemo(() => {
    if (!activeCall) return [];
    const arrivalIds = getTerminalCallArrivalStateContainerIds(activeCall);
    const onVessel = (containers || []).filter(
      (c) =>
        c.position?.length > 0 &&
        c.vessel?.["@id"] === vesselForStability?.["@id"] &&
        (arrivalIds.includes(c["@id"]) || activeCall.loadingContainers?.some((ref: any) => ref["@id"] === c["@id"]))
    );
    const unloadingList = activeCall.unloadingContainers || [];
    if (unloadingList.length === 0) return onVessel;
    const unloadIds = new Set(
      unloadingList.map((container: any) => container?.["@id"])
    );
    return onVessel.filter(
      (container: any) => !unloadIds.has(container["@id"])
    );
  }, [containers, vesselForStability, activeCall]);
  const listedContainers = showAllContainers
    ? visibleContainers
    : visibleContainers.slice(0, CONTAINER_LIST_LIMIT);
  const remainingContainerCount = Math.max(
    visibleContainers.length - listedContainers.length,
    0
  );
  const terminalReference = activeCall?.terminal;
  const terminal =
    terminals.find(
      (terminalOption) =>
        getId(terminalOption) &&
        getId(terminalOption) === getId(terminalReference)
    ) ||
    (terminalReference && typeof terminalReference === "object"
      ? terminalReference
      : {});
  const contacts = Array.isArray(terminal?.contacts)
    ? terminal.contacts
    : [];
  const primaryContact = contacts[0] || {};
  const primaryContactPhone = primaryContact.phoneNumber || "";
  const normalizedStatus = String(activeCall?.status || "").toLowerCase();
  const completed = ["completed", "complete", "done"].includes(normalizedStatus) || Boolean(activeCall?.departure);
  const statusLabel = completed
    ? t("completed", "Completed")
    : activeCall?.status
      ? t(activeCall.status, activeCall.status)
      : t("planned", "Planned");
  const startDate = activeCall?.planned || activeCall?.arrival || activeCall?.operationStart;
  const endDate = activeCall?.departure || activeCall?.operationEnd;
  const validStartDate = startDate && dayjs(startDate).isValid();
  const validEndDate = endDate && dayjs(endDate).isValid();
  const terminalCountry =
    typeof terminal.country === "string"
      ? terminal.country
      : terminal.country?.name || terminal.country?.label || terminal.country?.code;
  const terminalLocation = [terminal.city, terminalCountry].filter(Boolean).join(", ");
  const latitude = Number(terminal.latitude);
  const longitude = Number(terminal.longitude);
  const hasCoordinates = Number.isFinite(latitude) && Number.isFinite(longitude);
  const mapCoordinates: [number, number] = hasCoordinates
    ? [latitude, longitude]
    : [51.23102, 4.3979];

  const totalWeight = (items: any[]) =>
    items.reduce((total, container) => total + Number(container?.weight || 0), 0) / 1000;
  const totalTeu = (items: any[]) =>
    items.reduce((total, container) => total + Number(container?.containerType?.teu || 0), 0);

  return (
    <Dialog open={open} onClose={onClose} fullScreen className="mobile-terminal-detail-dialog">
      <AppBar position="sticky" elevation={0} className="mobile-detail-appbar">
        <Toolbar className="mobile-detail-toolbar">
          <IconButton color="inherit" onClick={onClose} aria-label={t("back", "Back")}><BackIcon /></IconButton>
          <CompleteIcon className={completed ? "is-complete" : ""} />
          <Box className="mobile-detail-title mobile-detail-title--left">
            <Typography>{terminal.name || t("terminal", "Terminal")}</Typography>
            <span>{terminal.abbreviation || terminal.city || "—"}</span>
          </Box>
          <IconButton color="inherit" onClick={() => setTimingOpen(true)} aria-label={t("editTimings", "Edit timings")}><EditIcon /></IconButton>
          <IconButton color="inherit" aria-label={t("more", "More")}><MoreIcon /></IconButton>
        </Toolbar>
      </AppBar>

      <Box className="mobile-terminal-detail-page">
        <Box className="mobile-terminal-detail-summary">
          <Box className="mobile-terminal-status-row">
            <span className={`mobile-terminal-status${completed ? " is-complete" : ""}`}>
              {statusLabel}
            </span>
            <span>{validStartDate ? dayjs(startDate).format("DD MMM YYYY") : "—"}</span>
            <span>·</span>
            <span>
              {validStartDate ? dayjs(startDate).format("HH:mm") : "—"}
              {validEndDate ? ` – ${dayjs(endDate).format("HH:mm")}` : ""}
            </span>
          </Box>
          <Typography className="mobile-terminal-location-line">{terminalLocation || "—"}</Typography>
        </Box>

        <Box className="mobile-terminal-detail-scroll">

        <section className="mobile-detail-section">
          <header className="mobile-detail-section-title"><ContactIcon /><span>{t("terminalContact", "Terminal contact")}</span></header>
          <Box className="mobile-contact-card">
            <div><strong>{primaryContact.name || "—"}</strong></div>
            <div className="mobile-contact-action"><PhoneIcon /><span>{primaryContactPhone || "—"}</span>{primaryContactPhone ? <Button component="a" href={`tel:${primaryContactPhone}`}>{t("call", "Call")}</Button> : null}</div>
            <div className="mobile-contact-action"><EmailIcon /><span>{primaryContact.email || "—"}</span>{primaryContact.email ? <Button component="a" href={`mailto:${primaryContact.email}`}>{t("email", "Email")}</Button> : null}</div>
          </Box>
        </section>

        <section className="mobile-detail-section">
          <header className="mobile-detail-section-title"><LocationIcon /><span>{t("location", "Location")}</span></header>
          <Box className="mobile-location-map">
            <MapContainer
              key={`${getId(terminal)}-${mapCoordinates[0]}-${mapCoordinates[1]}`}
              center={mapCoordinates}
              zoom={12}
              scrollWheelZoom={false}
              style={{ height: "100%", width: "100%" }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <Marker position={mapCoordinates}>
                <Popup>{terminal.name || terminalLocation || t("terminal", "Terminal")}</Popup>
              </Marker>
            </MapContainer>
            <span>{hasCoordinates ? `${latitude.toFixed(4)}, ${longitude.toFixed(4)}` : terminalLocation || terminal.name || "—"}</span>
          </Box>
        </section>

        <section className="mobile-detail-section">
          <header className="mobile-detail-section-title"><TimeIcon /><span>{t("timings", "Timings")}</span></header>
          <Box className="mobile-detail-timeline">
            {timingRows.map((row) => (
              <button type="button" key={row.key} onClick={() => setTimingOpen(true)}>
                <i className={`mobile-detail-timeline-dot is-${row.color}`} />
                <span>{t(row.key, row.label)}</span>
                <strong>{formatDateTime(activeCall?.[row.key])}</strong>
                <CompleteIcon className={activeCall?.[row.key] ? "is-complete" : ""} />
              </button>
            ))}
          </Box>
        </section>

        <section className="mobile-detail-section mobile-cargo-section">
          <header className="mobile-detail-section-title"><ContainerIcon /><span>{t("containers", "Containers")}</span></header>
          <Box className="mobile-terminal-cargo-counts">
            <div className="is-loading">
              <ArrowDownIcon />
              <span>{t("load", "Load")}</span>
              <strong>{loadingContainers.length}</strong>
              <b>{totalTeu(loadingContainers)} TEU</b>
            </div>
            <div className="is-unloading">
              <ArrowUpIcon />
              <span>{t("unload", "Unload")}</span>
              <strong>{unloadingContainers.length}</strong>
              <b>{totalTeu(unloadingContainers)} TEU</b>
            </div>
          </Box>
          <Tabs
            value={cargoTab}
            onChange={(_, value) => {
              setCargoTab(value);
              setShowAllContainers(false);
            }}
            variant="fullWidth"
            className={`mobile-cargo-tabs ${cargoTab === 0 ? "is-loading" : "is-unloading"}`}
          >
            <Tab label={`${t("loading", "Loading")} (${loadingContainers.length})`} />
            <Tab label={`${t("unloading", "Unloading")} (${unloadingContainers.length})`} />
          </Tabs>
          <Box className="mobile-cargo-table-head"><span>{t("containerId", "Container ID")}</span><span>{t("type", "Type")}</span><span>{t("weight", "Weight")}</span><span>{t("position", "Position")}</span></Box>
          <Box className="mobile-cargo-list">
            {listedContainers.map((container) => (
              <button
                type="button"
                key={getId(container) || container.uid}
                onClick={() => setSelectedContainer(container)}
                style={{
                  borderLeft: `2px solid ${
                    cargoTab === 0
                      ? container.unloadingTerminalCall?.terminal?.color ||
                        container.unloadingTerminal?.color ||
                        "transparent"
                      : container.loadingTerminalCall?.terminal?.color ||
                        container.loadingTerminal?.color ||
                        "transparent"
                  }`,
                }}
              >
                <span>{container.uid || "—"}</span>
                <span>{container.containerType?.abbreviation || container.containerType?.name || "—"}</span>
                <span>{((Number(container.weight) || 0) / 1000).toFixed(2)} T</span>
                <span>{Array.isArray(container.position) ? container.position.join(" · ") : "—"}</span>
                <ChevronIcon />
              </button>
            ))}
            {!visibleContainers.length && <Typography className="mobile-empty-message">{t("noContainers", "No containers")}</Typography>}
          </Box>
          {remainingContainerCount > 0 ? (
            <Button
              fullWidth
              className={`mobile-load-all-containers ${cargoTab === 0 ? "is-loading" : "is-unloading"}`}
              onClick={() => setShowAllContainers(true)}
            >
              {t("loadAllContainers", {
                count: remainingContainerCount,
                defaultValue: "Load all containers ({{count}} more)",
              })}
            </Button>
          ) : null}
          <Box className="mobile-cargo-summary"><span>{visibleContainers.length} {t("containers", "containers")}</span><strong>{totalWeight(visibleContainers).toFixed(1)} T</strong></Box>
        </section>

        <section className="mobile-detail-section">
          <header className="mobile-detail-section-title"><VesselIcon /><span>{t("positionsOnBoard", "Positions on board")}</span></header>
          <MobileVesselPositionGrid
            vessel={vessel || activeCall?.vessel}
            containers={visibleContainers}
            direction={cargoTab === 0 ? "loading" : "unloading"}
            onContainerClick={setSelectedContainer}
            dockingSide={activeCall?.dockingSide || "port"}
          />
        </section>

        {stabilityContainers.length > 0 && (
          <section className="mobile-detail-section">
            <header className="mobile-detail-section-title"><BalanceIcon /><span>{t("stability", "Stability")}</span></header>
            <Box onClick={() => setStabilityDetailOpen(true)} sx={{ cursor: "pointer" }}>
              <MobileVesselLoadStability
                vessel={vesselForStability}
                containers={stabilityContainers}
                terminalCall={activeCall}
              />
            </Box>
          </section>
        )}

        <Dialog open={stabilityDetailOpen} onClose={() => setStabilityDetailOpen(false)} fullScreen>
          <AppBar position="sticky" elevation={0} className="mobile-detail-appbar">
            <Toolbar>
              <Typography sx={{ flex: 1, fontWeight: 700 }}>{t("stability", "Stability")}</Typography>
              <IconButton color="inherit" onClick={() => setStabilityDetailOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Toolbar>
          </AppBar>
          <Box sx={{ p: 2, overflowY: "auto" }}>
            <VesselLoadStability
              vessel={vesselForStability}
              containers={stabilityContainers}
              terminalCall={activeCall}
            />
          </Box>
        </Dialog>
        </Box>
      </Box>

      <MobileBottomNavigation active="timeline" onAdd={onAdd} />

      <MobileTerminalTimingDialog
        open={timingOpen}
        terminalCall={activeCall}
        onClose={() => setTimingOpen(false)}
        onSaved={setActiveCall}
      />

      <MobileContainerDetailsDialog
        open={Boolean(selectedContainer)}
        container={selectedContainer}
        vessel={vessel || activeCall?.vessel}
        terminalCall={activeCall}
        terminalCalls={terminalCalls}
        terminals={terminals}
        direction={cargoTab === 0 ? "loading" : "unloading"}
        onClose={() => setSelectedContainer(null)}
        onManage={() => onManageContainers?.(activeCall)}
      />
    </Dialog>
  );
};

export default MobileTerminalCallDetails;
